import {
  inject,
  makeEnvironmentProviders,
  provideEnvironmentInitializer,
  type EnvironmentProviders,
} from '@angular/core';

import { APP_CONFIG } from '../config/app-config.model';
import { FilterScopeController, FilterScopeService } from './filter-scope.service';
import { FilterOptionsAdapter, GeneratedFilterOptionsAdapter } from './filter-options.adapter';
import { FilterOptionsService } from './filter-options.service';
import {
  createFilterTelemetrySink,
  FILTER_TELEMETRY_SINK,
  FilterTelemetry,
} from './filter-telemetry';
import { ClientRouteScope } from './client-route-scope';
import { FilterUrlProjection } from './filter-url.projection';
import { AccessTokenProvider } from '../realtime/access-token.provider';

/**
 * Wires the shared filter state (AITH-44), following the repository's
 * `*.providers.ts` convention (`notification.providers.ts`,
 * `realtime.providers.ts`).
 *
 * `FilterScopeService` is an abstract port with no `providedIn`, so it needs an
 * explicit binding: {@link FilterScopeController} is the single root instance
 * (FR-009) and the port is an alias onto it. Consumers inject the port and read
 * `scope()`; the bar's own controls inject the controller and write.
 */
export function provideAithFilters(): EnvironmentProviders {
  return makeEnvironmentProviders([
    FilterScopeController,
    { provide: FilterScopeService, useExisting: FilterScopeController },
    FilterTelemetry,
    {
      provide: FILTER_TELEMETRY_SINK,
      useFactory: () => createFilterTelemetrySink(inject(APP_CONFIG).applicationInsightsConnectionString),
    },
    FilterOptionsService,
    {
      provide: FilterOptionsAdapter,
      useFactory: () =>
        new GeneratedFilterOptionsAdapter(inject(APP_CONFIG).apiBaseUrl, inject(AccessTokenProvider)),
    },
    FilterUrlProjection,
    ClientRouteScope,
    // Once per app bootstrap, NOT per bar instance: the opt-out routes must
    // neither reset nor re-read the shared state (R-011), and the URL must be
    // read before the first destination's components subscribe (FR-012).
    provideEnvironmentInitializer(() => inject(FilterUrlProjection).initialize()),
    // AITH-46 D-06: the path is a client-scope source too, so a drill-down to
    // /clients/:clientCode does not land with three inert selectors. Registered
    // AFTER the URL projection so a `client` query parameter in a shared link is
    // read first and the path then agrees with it (they cannot disagree — the
    // link that carries both was written from this same state).
    provideEnvironmentInitializer(() => inject(ClientRouteScope).initialize()),
  ]);
}
